import { useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import isoWeek from 'dayjs/plugin/isoWeek'
import isBetween from 'dayjs/plugin/isBetween'
import { io, Socket as SocketIo } from 'socket.io-client'
import {
  ChangedInterval,
  ChangedResource,
} from '@lmc/ts-common/lib/utils/rota-socket-response'

import useFeatureFlags from 'hooks/useFeatureFlags'
import CONSTANTS from 'utils/constants'
import { getQueryInvalidationPredicate } from './utils'
import { resourceToQueryKeyMap } from './constants'
import { useLocation } from 'react-router-dom'

dayjs.extend(weekday)
dayjs.extend(isoWeek)
dayjs.extend(isBetween)

export type InvalidateMessage = {
  changedResource: ChangedResource
  changedInterval?: ChangedInterval
  day?: string
  startDate?: string
  endDate?: string
}

const useQueryInvalidation = () => {
  const queryClient = useQueryClient()
  const userID: string = useSelector((state: any) => state.global.user.id)
  const socketRef = useRef<SocketIo | null>(null)
	const location = useLocation()
	
  const flags = useFeatureFlags({
    flagNames: ['rostering_web_socket'],
  })?.data

  const flagsWebSocket = flags?.rostering_web_socket?.enabled


  useEffect(() => {
		const url = CONSTANTS.VITE_APP_ROTA_API_WS
		const path = CONSTANTS.VITE_APP_ROTA_API_WS_PATH

    if (
			!socketRef.current &&
			userID
		) {
			socketRef.current = io(url, {
				path,
				query: {
					user_id: userID
				},
				autoConnect: false,
				transports: ['websocket'],
			})

    }

		return () => {
			if (socketRef.current) {
				socketRef.current.disconnect()
				socketRef.current = null
			}
		}
	}, [userID])

	useEffect(() => {
		if (!flagsWebSocket) return
		
		const isRota = location.pathname.startsWith('/rota')

		if (socketRef.current && isRota && !socketRef.current.connected) {
			socketRef.current = socketRef.current.connect()
		}
	}, [location, flagsWebSocket])

  useEffect(() => {
    if (flagsWebSocket && socketRef.current) {
      const onInvalidateQuery = (message: InvalidateMessage) => {
        const queryKeysToInvalidate =
          resourceToQueryKeyMap[message.changedResource]

        if (!queryKeysToInvalidate?.length) return

        queryClient.invalidateQueries({
          predicate: getQueryInvalidationPredicate(message),
        })
      }

      const handleConnect = () => {
        socketRef.current?.on('Invalidate', onInvalidateQuery)
      }

      socketRef.current.on('connect', handleConnect)

      return () => {
        socketRef.current?.off('connect', handleConnect)
        socketRef.current?.off('Invalidate', onInvalidateQuery)
      }
    }

    return () => {}
  }, [socketRef.current, flagsWebSocket])
}

export default useQueryInvalidation
